import { Link } from 'react-router-dom';
import { CookingPot } from 'lucide-react';
import { recipePath, type CookLogEntry } from '@awwyeah/shared';
import { formatClock } from '../feed';
import type { MemberDirectory } from '../members';

/**
 * A cook, in the feed (owner ask 2026-08-18: "I made smoked salmon today!").
 *
 * The same quiet fact card as `GameFeedCard`, deliberately — one line of plain sentence, a
 * glyph, a clock, the whole row tappable and nothing else interactive. No photo, no
 * seen-by (see `../feedItems.ts` for the reasoning, which is *not* the same reasoning as
 * games'), and above all no count: this card says a thing happened once, on a day, and
 * says it exactly once no matter how often the family cooks it.
 *
 * The title comes from `cook.recipeTitle`, snapshotted when the cook was logged, so a
 * deleted or renamed recipe leaves the memory intact and only costs the link its
 * destination. The link is rendered unconditionally on purpose: the feed has no view of
 * the recipe box (it never opens `RECIPES_DOC`), and a detail page that says "This recipe
 * isn't here anymore" is a better answer to a tap than a row that silently does nothing.
 */
export function CookFeedCard({
  cook,
  members,
}: {
  cook: CookLogEntry;
  members: MemberDirectory;
}): JSX.Element {
  const cookedBy = members.get(cook.cookedBy);

  return (
    <Link
      to={recipePath(cook.recipeId)}
      data-testid="checkin-cook-card"
      data-cook-id={cook.id}
      className="flex items-center gap-2.5 border-y border-line bg-surface px-4 py-3"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-raised text-fg-2">
        <CookingPot size={16} strokeWidth={1.75} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] leading-[1.35] text-fg">
          <span style={{ color: cookedBy.color }} className="font-semibold">
            {cookedBy.name}
          </span>
          <span className="text-fg-2"> cooked </span>
          <span className="text-fg">{cook.recipeTitle}</span>
        </div>
        <div className="truncate text-xs text-fg-3">
          {cook.note ? `${cook.note} · ` : ''}
          {formatClock(cook.cookedAt)}
        </div>
      </div>
    </Link>
  );
}
